import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const SafeLandingPage: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <div className="min-h-screen bg-white">
      {/* Header Navigation */}
      <header className="fixed top-0 left-0 right-0 md:-top-10 md:-left-20 z-50 transition-all duration-300 bg-white/80 backdrop-blur shadow-sm">
        <div className="max-w-7xl mx-auto px-1 sm:px-2 lg:px-3 relative">
          <button
            className="md:hidden absolute left-2 top-2 z-[60] p-2 rounded-md bg-white/90 shadow hover:bg-white"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
          <div className="flex justify-between items-center py-2 md:py-1">
            {/* Logo - Left side, large */}
            <div className="flex items-center pl-12 md:pl-0">
              {/* Mobile logo - simple tick with text like LaunchPage */}
              <div className="flex md:hidden items-center gap-2">
                <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">CosmaComply</div>
                  <div className="text-xs text-gray-500">MoCRA Compliance</div>
                </div>
              </div>
              {/* Desktop logo (unchanged) */}
              <img src="/OYAfnU01.svg" alt="CosmaComply" className="hidden md:block" style={{width: '500px', height: '200px'}} />
            </div>
            {/* Desktop Navigation - Right side */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#modules" className="text-gray-700 hover:text-indigo-600 font-medium transition-colors">Modules</a>
              <a href="#about" className="text-gray-700 hover:text-indigo-600 font-medium transition-colors">About</a>
              <Link to="/mocra" className="text-gray-700 hover:text-indigo-600 font-medium transition-colors">MoCRA</Link>
              <a href="#faq" className="text-gray-700 hover:text-indigo-600 font-medium transition-colors">FAQ</a>
              <a href="#contact" className="text-gray-700 hover:text-indigo-600 font-medium transition-colors">Contact</a>
              <Link to="/signup" className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium">Get Started</Link>
            </nav>
          </div>
          {/* Mobile menu */}
          <div className={`${mobileOpen ? 'block' : 'hidden'} md:hidden border-t border-gray-200 pb-2`}> 
            <div className="py-2 space-y-1 px-2">
              <a href="#modules" className="block px-2 py-2 text-gray-700 hover:text-indigo-600">Modules</a>
              <a href="#about" className="block px-2 py-2 text-gray-700 hover:text-indigo-600">About</a>
              <Link to="/mocra" className="block px-2 py-2 text-gray-700 hover:text-indigo-600">MoCRA</Link>
              <a href="#faq" className="block px-2 py-2 text-gray-700 hover:text-indigo-600">FAQ</a>
              <a href="#contact" className="block px-2 py-2 text-gray-700 hover:text-indigo-600">Contact</a>
              <div className="flex items-center gap-3 px-2 pt-2">
                <Link to="/login" className="text-gray-700 hover:text-indigo-600">Sign in</Link>
                <Link to="/signup" className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium">Get Started</Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-48 pb-16 bg-gradient-to-br from-indigo-50 via-white to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/5 to-blue-600/5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight animate-fade-in">
              Transform MoCRA Compliance from a{' '}
              <span className="text-red-600">$50,000 Liability</span> into{' '}
              <span className="text-green-600">$38,400 Annual Savings</span>
            </h1>
            <p className="text-xl text-gray-600 mb-4 max-w-4xl mx-auto leading-relaxed animate-fade-in-delay">
              An AI-assisted platform that helps streamline MoCRA workflows, track deadlines, and reduce risk of penalties.
              Built for cosmetic manufacturers.
            </p>
            <p className="text-base text-gray-700 mb-8 max-w-3xl mx-auto">
              MoCRA compliance software for cosmetics companies: cosmetic product listing, facility registration, adverse event reporting (cosmetovigilance), and toxicological risk assessment (TRA) with safety dossiers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8 animate-fade-in-delay-2">
              <a href="#modules" className="bg-indigo-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg">🎯 Explore Modules</a>
              <a href="#demo" className="border-2 border-indigo-600 text-indigo-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-indigo-50 transition-colors">📺 Watch Demo</a>
            </div>
            
            <p className="text-sm text-gray-500">CosmaComply is not FDA-approved and is not affiliated with the FDA.</p>
          </div>
        </div>
      </section>

      {/* Demo Monitor Placeholder */}
      <section id="demo" className="scroll-mt-24 pb-8">
        <div className="max-w-5xl mx-auto px-4">
          <div className="monitor-frame p-2">
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-gray-800 via-gray-900 to-black flex items-center justify-center text-gray-300">
                <div className="text-center">
                  <div className="text-2xl font-semibold mb-2">Product Demo</div>
                  <div className="text-sm opacity-80">See how CosmaComply streamlines MoCRA workflows</div>
                </div>
              </div>
              <a
                href="https://www.youtube.com/@CosmaComply"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Play demo video"
                className="play-button"
              >
                <span className="triangle" />
              </a>
            </div>
          </div>
          <div className="monitor-stand"></div>
          <p className="text-sm text-gray-500 mt-3 text-center">Illustrative demo. Actual results may vary.</p>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="animate-fade-in-up">
              <div className="text-4xl mb-3">🏆</div>
              <div className="text-lg font-semibold text-gray-900 mb-2">MoCRA-aligned Workflows</div>
              <div className="text-gray-600">Built to support FDA MoCRA compliance tasks. Not FDA-approved.</div>
            </div>
            <div className="animate-fade-in-up" style={{animationDelay: '0.2s'}}>
              <div className="text-4xl mb-3">🔒</div>
              <div className="text-lg font-semibold text-gray-900 mb-2">Enterprise Security</div>
              <div className="text-gray-600">Industry-standard encryption (in transit & at rest) and data isolation</div>
            </div>
            <div className="animate-fade-in-up" style={{animationDelay: '0.4s'}}>
              <div className="text-4xl mb-3">⚡</div>
              <div className="text-lg font-semibold text-gray-900 mb-2">Quick Setup</div>
              <div className="text-gray-600">Get started in minutes, not months</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="modules" className="scroll-mt-24 py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Everything You Need for MoCRA Compliance</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">From facility registration to adverse event reporting, we've got you covered</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-4xl mb-6">🏢</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Facility Registration</h3>
              <p className="text-gray-600 text-lg leading-relaxed">Guided FDA registration workflows with small business exemption calculation support and renewal reminders.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-4xl mb-6">⚠️</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">SAE Detection</h3>
              <p className="text-gray-600 text-lg leading-relaxed">AI-assisted severe adverse event detection with 15-day FDA deadline tracking to help reduce risk of penalties.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-4xl mb-6">📋</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Product Management</h3>
              <p className="text-gray-600 text-lg leading-relaxed">Complete ingredient tracking and safety documentation management. Centralized compliance for your portfolio.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-4xl mb-6">🤖</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AI Assistant</h3>
              <p className="text-gray-600 text-lg leading-relaxed">Get instant answers to compliance questions. AI-powered guidance tailored to your situation.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-4xl mb-6">📊</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Analytics Dashboard</h3>
              <p className="text-gray-600 text-lg leading-relaxed">Compliance scoring and risk assessment. Executive insights for decision making.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-4xl mb-6">📝</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Audit Trail</h3>
              <p className="text-gray-600 text-lg leading-relaxed">Comprehensive logging for every action to support regulatory requirements.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="mocra" className="scroll-mt-24 py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Cosmetics Regulatory Compliance under MoCRA</h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">MoCRA sets requirements for cosmetic companies, including facility registration, cosmetic product listing, safety substantiation, and adverse event reporting. CosmaComply helps teams operationalize these requirements with guided workflows and audit trails.</p>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Cosmetic Product Listing & Facility Registration</h3>
              <p className="text-gray-700 leading-relaxed mb-4">Prepare and manage FDA-ready product listing and facility registration data with structured templates and reminders for renewals.</p>
            </div>
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Adverse Event Reporting & Cosmetovigilance</h3>
              <p className="text-gray-700 leading-relaxed mb-4">Detect potential serious adverse events, triage, and track 15-day reporting timelines. Maintain an auditable history of case actions.</p>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Safety Dossier & Toxicological Risk Assessment (TRA)</h3>
              <p className="text-gray-700 leading-relaxed">Generate TRA calculations (SED, MoS) and assemble safety substantiation documents with professional PDF reports.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-10">How CosmaComply Helps</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-10 rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-300">
              <div className="text-2xl font-semibold text-gray-900 mb-3">Faster Workflows</div>
              <div className="text-gray-600 text-lg">Centralized modules, templates, and automation to speed up routine tasks.</div>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-300">
              <div className="text-2xl font-semibold text-gray-900 mb-3">Lower Compliance Ops Cost</div>
              <div className="text-gray-600 text-lg">Reduce manual effort with tracking, reminders, and smart validation.</div>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-300">
              <div className="text-2xl font-semibold text-gray-900 mb-3">Higher Data Consistency</div>
              <div className="text-gray-600 text-lg">AI-assisted checks and audit trails to support consistent, high-quality records.</div>
            </div>
          </div>
        </div>
      </section>
      
      {/* About Section */}
      <section id="about" className="scroll-mt-24 py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">About CosmaComply</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">We build AI-assisted, MoCRA-aligned workflows to help cosmetic teams streamline compliance and improve data consistency.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Our Mission</h3>
              <p className="text-gray-600">Empower manufacturers with intelligent tools that reduce manual effort and support regulatory requirements without replacing good quality systems and processes.</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Why CosmaComply?</h4>
              <ul className="list-disc pl-5 text-gray-600 space-y-1">
                <li>MoCRA-aligned workflows</li>
                <li>AI-assisted checks and reminders</li>
                <li>Audit trails and reporting</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="scroll-mt-24 py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">MoCRA FAQs</h2>
          <div className="space-y-4">
            <details className="border border-gray-200 rounded-lg p-4">
              <summary className="font-semibold text-gray-900 cursor-pointer">What is MoCRA?</summary>
              <p className="mt-2 text-gray-700">MoCRA is the Modernization of Cosmetics Regulation Act in the United States. It updates FDA oversight of cosmetics including registration, listing, safety substantiation, and adverse event reporting.</p>
            </details>
            <details className="border border-gray-200 rounded-lg p-4">
              <summary className="font-semibold text-gray-900 cursor-pointer">Who must comply with MoCRA?</summary>
              <p className="mt-2 text-gray-700">Most cosmetics manufacturers and responsible persons marketing cosmetics in the U.S. must comply. Certain small business exemptions may apply in specific areas.</p>
            </details>
            <details className="border border-gray-200 rounded-lg p-4">
              <summary className="font-semibold text-gray-900 cursor-pointer">What does facility registration involve?</summary>
              <p className="mt-2 text-gray-700">Facilities manufacturing or processing cosmetics must register with the FDA and renew periodically. Maintaining accurate records and renewals is essential.</p>
            </details>
            <details className="border border-gray-200 rounded-lg p-4">
              <summary className="font-semibold text-gray-900 cursor-pointer">What is cosmetic product listing?</summary>
              <p className="mt-2 text-gray-700">Responsible persons must submit product listing information to FDA for marketed cosmetic products and keep listings updated.</p>
            </details>
            <details className="border border-gray-200 rounded-lg p-4">
              <summary className="font-semibold text-gray-900 cursor-pointer">How are adverse events handled?</summary>
              <p className="mt-2 text-gray-700">Potential serious adverse events should be evaluated, and reportable cases must be submitted to FDA within specified timelines with appropriate follow-up.</p>
            </details>
            <details className="border border-gray-200 rounded-lg p-4">
              <summary className="font-semibold text-gray-900 cursor-pointer">Does CosmaComply automate TRA and safety dossiers?</summary>
              <p className="mt-2 text-gray-700">Yes—CosmaComply helps calculate SED/MoS and assemble safety substantiation documents, generating professional PDFs for review. CosmaComply is not affiliated with or approved by the FDA.</p>
            </details>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="scroll-mt-24 py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Get in Touch</h2>
            <p className="text-gray-600">Questions or want a walkthrough? We’re here to help.</p>
          </div>
          <div className="flex justify-center gap-3">
            <a href="mailto:cosmacomply@gmail.com" className="border border-gray-300 text-gray-700 px-5 py-3 rounded-lg hover:bg-gray-100">Email us</a>
            <Link to="/signup" className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700">Schedule Demo</Link>
          </div>
        </div>
      </section>

      

      {/* Footer */}
      <footer className="bg-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <div className="text-3xl font-bold text-white mb-6">CosmaComply</div>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">The MoCRA compliance platform for cosmetic manufacturers.</p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-6 text-lg">Product</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#modules" className="hover:text-white transition-colors text-lg">Features</a></li>
                <li><Link to="/mocra" className="hover:text-white transition-colors text-lg">MoCRA</Link></li>
                <li><Link to="/integrations" className="hover:text-white transition-colors text-lg">Integrations</Link></li>
                <li><Link to="/security" className="hover:text-white transition-colors text-lg">Security</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-6 text-lg">Company</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#about" className="hover:text-white transition-colors text-lg">About</a></li>
                <li><Link to="/blog" className="hover:text-white transition-colors text-lg">Blog</Link></li>
                <li><Link to="/careers" className="hover:text-white transition-colors text-lg">Careers</Link></li>
                <li><a href="#contact" className="hover:text-white transition-colors text-lg">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-6 text-lg">Support</h4>
              <ul className="space-y-3 text-gray-400">
                <li><Link to="/help" className="hover:text-white transition-colors text-lg">Help Center</Link></li>
                <li><Link to="/docs" className="hover:text-white transition-colors text-lg">Documentation</Link></li>
                <li><Link to="/status" className="hover:text-white transition-colors text-lg">Status</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors text-lg">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors text-lg">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-12 pt-8 text-center text-gray-400">
            <p className="text-lg">&copy; {new Date().getFullYear()} CosmaComply. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Custom CSS for animations and demo monitor */}
      <style>{`
        @keyframes fade-in { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fade-in-up { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.8s ease-out; }
        .animate-fade-in-delay { animation: fade-in 0.8s ease-out 0.2s both; }
        .animate-fade-in-delay-2 { animation: fade-in 0.8s ease-out 0.4s both; }
        .animate-fade-in-delay-3 { animation: fade-in 0.8s ease-out 0.6s both; }
        .animate-fade-in-up { animation: fade-in-up 0.8s ease-out both; }

        .monitor-frame { background: linear-gradient(180deg, #0b0f1a, #131a2a); border-radius: 18px; box-shadow: 0 10px 30px rgba(2,6,23,0.3); border: 1px solid rgba(255,255,255,0.06); }
        .monitor-stand { width: 180px; height: 10px; background: linear-gradient(180deg, #1f2937, #111827); margin: 12px auto 0; border-radius: 6px; }
        .play-button { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); height: 72px; width: 72px; border-radius: 9999px; background: rgba(255,255,255,0.9); display:flex; align-items:center; justify-content:center; box-shadow: 0 10px 30px rgba(0,0,0,0.35); transition: transform .2s ease, background .2s ease; }
        .play-button:hover { transform: translate(-50%, -50%) scale(1.05); background: #ffffff; }
        .triangle { width: 0; height: 0; border-left: 18px solid #4f46e5; border-top: 12px solid transparent; border-bottom: 12px solid transparent; margin-left: 4px; }
      `}</style>
    </div>
  );
};

export default SafeLandingPage;
